import { HelpCircle, StickyNote, BookText } from "lucide-react";

export const CornellNotes = ({ lecture }) => {
  const concepts = lecture.key_concepts || [];
  const terms = lecture.key_terms || [];

  // Cues: term names + concept-derived prompts
  const cues = [
    ...concepts.map((c) => ({ type: "concept", text: `What is ${c.concept}?` })),
    ...terms.map((t) => ({ type: "term", text: t.term })),
  ];

  return (
    <div data-testid="cornell-notes" className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="border-b border-border px-6 py-4 bg-secondary/40">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Cornell Notes</p>
        <h3 className="font-serif text-2xl font-semibold">{lecture.title}</h3>
        <p className="text-sm text-muted-foreground">{lecture.book_title} · {lecture.chapters || "Whole book"}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3">
        {/* Cue column */}
        <div className="md:col-span-1 border-b md:border-b-0 md:border-r border-border p-5">
          <div className="flex items-center gap-2 mb-4 text-accent">
            <HelpCircle className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Cues & Questions</span>
          </div>
          <ul className="space-y-3">
            {cues.map((c, i) => (
              <li key={i} className="text-sm leading-snug flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                <span className={c.type === "term" ? "font-mono font-semibold" : "text-foreground/85"}>{c.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Notes column */}
        <div className="md:col-span-2 p-5">
          <div className="flex items-center gap-2 mb-4 text-accent">
            <StickyNote className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Notes</span>
          </div>
          <div className="space-y-4">
            {concepts.map((c, i) => (
              <div key={i} className="print-break">
                <p className="font-semibold text-foreground">{c.concept}</p>
                <p className="text-sm text-foreground/80 leading-relaxed">{c.explanation}</p>
              </div>
            ))}
            {terms.length > 0 && (
              <div className="pt-2">
                <p className="font-semibold text-foreground mb-1">Key Terms</p>
                <dl className="space-y-1.5">
                  {terms.map((t, i) => (
                    <div key={i} className="text-sm">
                      <dt className="inline font-mono font-semibold">{t.term}: </dt>
                      <dd className="inline text-foreground/80">{t.definition}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary box */}
      <div className="border-t border-border p-5 bg-secondary/30">
        <div className="flex items-center gap-2 mb-2 text-accent">
          <BookText className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Summary</span>
        </div>
        <p className="text-sm text-foreground/85 leading-relaxed">{lecture.summary}</p>
      </div>
    </div>
  );
};
